import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getLanguage } from '../utils/locale';
import i18n, { AppLanguage } from '../i18n';
import { availableLanguages, langDef, resolveLanguage, LanguageDef, SOURCE_LANGUAGE } from '../i18n/languages';
import { useAuth } from './AuthContext';
import api from '../services/api';

const STORAGE_KEY = 'ui_lang';

interface LanguageContextType {
  language: AppLanguage;
  /** Languages this user may pick (see status in i18n/languages.ts) */
  languages: LanguageDef[];
  /** Whether this user may choose a language at all */
  canChoose: boolean;
  setLanguage: (code: string) => Promise<void>;
  /**
   * Pages with their own audience (the self-service analysis pages) show a language of their
   * own while they are open; null hands the language back to the panel. See useLanguageScope.
   */
  scopeLanguage: (code: string | null) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used within a LanguageProvider');
  return context;
};

function stored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function store(key: string, value: string | null) {
  try {
    if (value) localStorage.setItem(key, value); else localStorage.removeItem(key);
  } catch { /* storage may be blocked */ }
}

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, updateUser } = useAuth();
  const role = user?.role;
  const languages = useMemo(() => availableLanguages(role), [role]);
  const canChoose = languages.length > 1;

  // Signed in: only the saved account language. Signed out: this browser's last choice. A
  // language this user may not pick (e.g. a beta one after losing admin) falls back to Turkish.
  const [scoped, setScoped] = useState<string | null>(null);
  const wanted = resolveLanguage(scoped || (user ? user.language : stored(STORAGE_KEY)));
  const language: AppLanguage = languages.some((d) => d.code === wanted) ? wanted : SOURCE_LANGUAGE;
  const scopeLanguage = useCallback((code: string | null) => setScoped(code), []);

  // <html lang> starts as "tr" (index.html). Touch it only when the panel language actually
  // changes, so the public pages, which set it themselves (publicLang.ts), are left alone.
  const appliedLang = useRef<AppLanguage>(SOURCE_LANGUAGE);
  useEffect(() => {
    if (i18n.language !== language) i18n.changeLanguage(language);
    if (appliedLang.current !== language) {
      appliedLang.current = language;
      document.documentElement.lang = language;
      // Right-to-left hook: the page direction follows the language
      document.documentElement.dir = langDef(language).dir;
    }
  }, [language]);

  const setLanguage = useCallback(async (code: string) => {
    const next = resolveLanguage(code);
    if (next === language || !languages.some((d) => d.code === next)) return;
    const remember = () => store(STORAGE_KEY, next);
    await i18n.changeLanguage(next);
    if (!user) { remember(); return; }
    const previous = user;
    updateUser({ ...user, language: next });
    try {
      await api.updateLanguage(next);
      remember();
    } catch {
      updateUser(previous);
      await i18n.changeLanguage(language);
    }
  }, [languages, language, user, updateUser]);

  const value = useMemo(() => ({ language, languages, canChoose, setLanguage, scopeLanguage }), [language, languages, canChoose, setLanguage, scopeLanguage]);
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

/**
 * Shows the pages that call it in `code` while they are mounted, without touching the signed-in
 * panel user's language; leaving them returns to the panel's. A remount on a language switch
 * (LanguageBoundary) runs the cleanup and the new effect in one commit, so it does not flicker.
 */
export function useLanguageScope(code: string | null): void {
  const { scopeLanguage } = useLanguage();
  useEffect(() => {
    scopeLanguage(code);
    return () => scopeLanguage(null);
  }, [code, scopeLanguage]);
}

/**
 * Labels built outside components (status maps, i18n.t in helpers) only refresh on a render,
 * so the pages below are rebuilt once a new language has actually loaded. Placed under the
 * auth, socket and notification providers so a switch keeps the session and the live
 * connection. A Turkish session never remounts: the key starts and stays 'tr'.
 */
export const LanguageBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [renderedLang, setRenderedLang] = useState(getLanguage());
  useEffect(() => {
    const onChange = (lng: string) => setRenderedLang(resolveLanguage(lng));
    i18n.on('languageChanged', onChange);
    return () => { i18n.off('languageChanged', onChange); };
  }, []);
  return <React.Fragment key={renderedLang}>{children}</React.Fragment>;
};
